import { defineStore } from "pinia";
import {
  getCurrentUser,
  refreshMySubscriptions,
  UserInclude,
} from "@/repository";
import { computed, ref, watch } from "vue";
import { EndUser } from "@/types/user";
import { differenceInCalendarDays } from "date-fns";

export const useAuthStore = defineStore("auth", () => {
  const loginModalClosePressed = localStorage.getItem("loginModalClosePressed");
  const showLoginModal = ref(loginModalClosePressed !== "true");
  const showLogoutModal = ref(false);
  const user = ref<EndUser>();
  const isLoading = ref(false);

  const fetchUser = async () => {
    isLoading.value = true;
    try {
      const res = await getCurrentUser({
        include: [
          UserInclude.Subscriptions,
          UserInclude.EndUserPropertyValues,
          UserInclude.EndUserPropertyValuesEndUserProperty,
        ],
      });
      user.value = res.data.data;
    } catch (err) {
      // logic called in axios interceptor
    } finally {
      isLoading.value = false;
    }
  };

  const checkAuth = async () => {
    if (!isLoading.value) {
      return !!user.value;
    }

    const waitForLoading = new Promise((resolve) => {
      watch(isLoading, (value) => {
        if (!value) {
          resolve(!!user.value);
        }
      });
    });

    return await waitForLoading;
  };

  const refreshSubscriptions = async () => {
    const res = await refreshMySubscriptions();
    if (user.value) {
      user.value.subscriptions = res.data;
    }
  };

  const onCloseLoginModal = () => {
    localStorage.setItem("loginModalClosePressed", "true");
  };

  const hasActiveSubscription = computed(() => {
    return user.value?.subscriptions?.data?.length ?? 0 > 0;
  });

  const daysUntilSubscriptionExpiry = computed(() => {
    const endDate = user.value?.subscriptions?.data?.[0]?.end_date;
    if (!endDate) return null;
    const days = differenceInCalendarDays(new Date(endDate), new Date());
    return days >= 0 ? days : null;
  });

  const subscriptionExpiringSoon = computed(() => {
    return daysUntilSubscriptionExpiry.value !== null && daysUntilSubscriptionExpiry.value <= 14;
  });

  if (!user.value) {
    fetchUser();
  }

  return {
    user,
    isLoading,
    hasActiveSubscription,
    daysUntilSubscriptionExpiry,
    subscriptionExpiringSoon,
    showLoginModal,
    showLogoutModal,
    checkAuth,
    refreshSubscriptions,
    onCloseLoginModal,
  };
});
